import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Image, Clock, DollarSign, Star, X, Loader2 } from 'lucide-react';
import { servicesAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', price: '', duration: '', category: 'Hair',
    image_url: '', is_featured: false, is_active: true
  });

  const categories = ['Hair', 'Nails', 'Spa', 'Makeup'];

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAllAdmin();
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (service = null) => {
    if (service) {
      setEditing(service);
      setForm({
        name: service.name, description: service.description, price: service.price.toString(),
        duration: service.duration.toString(), category: service.category, image_url: service.image_url || '',
        is_featured: service.is_featured, is_active: service.is_active
      });
    } else {
      setEditing(null);
      setForm({ name: '', description: '', price: '', duration: '', category: 'Hair', image_url: '', is_featured: false, is_active: true });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.duration) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const data = { ...form, price: parseFloat(form.price), duration: parseInt(form.duration) };
      if (editing) {
        await servicesAPI.update(editing.id, data);
        toast.success('Service updated');
      } else {
        await servicesAPI.create(data);
        toast.success('Service created');
      }
      fetchServices();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`Delete "${service.name}"?`)) return;
    try {
      await servicesAPI.delete(service.id);
      toast.success('Service deleted');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const filteredServices = services.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#4A154B]">Services</h1>
          <p className="text-gray-600">Manage salon services</p>
        </div>
        <Button onClick={() => openModal()} className="btn-primary" data-testid="add-service-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Service
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search services..." className="pl-12" data-testid="service-search" />
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton h-64 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServices.map((service) => (
            <div key={service.id} className={`glass rounded-2xl overflow-hidden card-hover ${!service.is_active ? 'opacity-60' : ''}`} data-testid={`service-card-${service.id}`}>
              <div className="relative h-40">
                <img src={service.image_url || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400'} alt={service.name} className="w-full h-full object-cover" />
                {service.is_featured && (
                  <span className="absolute top-2 right-2 bg-[#C9A689] text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                    <Star className="w-3 h-3" /> Featured
                  </span>
                )}
                {!service.is_active && (
                  <span className="absolute top-2 left-2 bg-gray-500 text-white px-2 py-1 rounded-full text-xs">Inactive</span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-xs text-[#C9A689] font-medium uppercase">{service.category}</span>
                    <h3 className="font-serif text-lg text-[#4A154B]">{service.name}</h3>
                  </div>
                  <span className="font-semibold text-[#4A154B]">${service.price}</span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">{service.description}</p>
                <div className="flex items-center justify-between pt-3 border-t border-[#F5E6D3]">
                  <span className="text-sm text-gray-500 flex items-center gap-1"><Clock className="w-4 h-4" /> {service.duration} min</span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openModal(service)} data-testid={`edit-service-${service.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(service)} className="text-red-500 hover:text-red-700" data-testid={`delete-service-${service.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A154B]">
              {editing ? 'Edit Service' : 'Add Service'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="form-label">Name *</label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Service name" data-testid="service-name-input" />
              </div>
              <div>
                <label className="form-label">Price ($) *</label>
                <Input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} placeholder="65" min="0" step="0.01" data-testid="service-price-input" />
              </div>
              <div>
                <label className="form-label">Duration (min) *</label>
                <Input type="number" value={form.duration} onChange={(e) => setForm({...form, duration: e.target.value})} placeholder="60" min="0" data-testid="service-duration-input" />
              </div>
              <div className="col-span-2">
                <label className="form-label">Category *</label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="service-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="form-label">Description</label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Service description" rows={3} data-testid="service-description-input" />
              </div>
              <div className="col-span-2">
                <label className="form-label">Image URL</label>
                <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="https://..." data-testid="service-image-input" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_featured} onCheckedChange={(c) => setForm({...form, is_featured: c})} data-testid="service-featured-switch" />
                <span className="text-sm">Featured</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(c) => setForm({...form, is_active: c})} data-testid="service-active-switch" />
                <span className="text-sm">Active</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-primary" data-testid="save-service-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServices;
